import { useState } from 'react';
import { watchlistApi } from '@/api/watchlist';
import type { CandidateScore, ImpersonationClassification } from '@/types/watchlist';
import { IMPERSONATION_THRESHOLDS as T } from '@/lib/watchlistThresholds';
import { wl, classificationColor } from './theme';

const CLASSIFICATION_LABEL: Record<ImpersonationClassification, string> = {
  official: 'OFFICIAL',
  declared_fan: 'DECLARED FAN',
  impersonation: 'IMPERSONATION',
  scam_impersonation: 'SCAM IMPERSONATION',
  unrelated: 'UNRELATED',
};

const OVERRIDE_OPTIONS: ImpersonationClassification[] = ['official', 'declared_fan', 'impersonation', 'scam_impersonation'];

function SignalRow({ label, value, detail }: { label: string; value: boolean; detail?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5" style={{ borderBottom: `1px solid ${wl.borderSoft}` }}>
      <span style={{ color: wl.textMuted, fontSize: 12 }}>{label}</span>
      <span style={{ color: value ? wl.high : wl.textFaint, fontSize: 12, fontWeight: 700 }}>
        {value ? 'YES' : 'no'}{detail ? <span style={{ color: wl.textFaint, fontWeight: 400 }}> · {detail}</span> : null}
      </span>
    </div>
  );
}

/** A signal bar with an optional threshold tick mark — "why it was
 * flagged" style from the mockup. Only draws a tick when a real, confirmed
 * threshold constant exists (watchlistThresholds.ts) — a bar with no
 * confirmed threshold just shows the raw value, rather than guessing one. */
function ThresholdBar({ label, value, threshold, detail }: { label: string; value: number; threshold?: number; detail?: string }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const flagged = threshold != null && value >= threshold;
  return (
    <div className="py-1.5" style={{ borderBottom: `1px solid ${wl.borderSoft}` }}>
      <div className="flex items-center justify-between mb-1">
        <span style={{ color: wl.textMuted, fontSize: 12 }}>{label}</span>
        <span style={{ color: flagged ? wl.high : wl.textFaint, fontSize: 11.5, fontFamily: wl.font }}>
          {detail ?? `${(value * 100).toFixed(0)}%`}
        </span>
      </div>
      <div style={{ position: 'relative', height: 6, background: wl.borderSoft, borderRadius: 3 }}>
        <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: flagged ? wl.high : wl.textFaint, borderRadius: 3 }} />
        {threshold != null && (
          <div style={{ position: 'absolute', left: `${threshold * 100}%`, top: -2, bottom: -2, width: 1.5, background: wl.text, opacity: 0.6 }} />
        )}
      </div>
    </div>
  );
}

interface Props {
  score: CandidateScore;
  /** Pass these three together to enable the "click to override" control —
   * only meaningful for a PERSISTED candidate (watchlist_vip_detected_candidates),
   * not an ephemeral on-demand /impersonation/score result, which has no id
   * to patch. */
  override?: { profileId: string; candidateId: string; onOverridden?: () => void };
}

export function CandidateScoreResult({ score, override }: Props) {
  const [overriding, setOverriding] = useState(false);
  const [saving, setSaving] = useState(false);
  const c = classificationColor(score.classification);

  async function applyOverride(classification: ImpersonationClassification) {
    if (!override) return;
    setSaving(true);
    try {
      await watchlistApi.reviewVipDetectedCandidate(override.profileId, override.candidateId, {
        status: 'confirmed', overridden_classification: classification,
      });
      setOverriding(false);
      override.onOverridden?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px', fontFamily: wl.font }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <span
          className="inline-flex items-center px-3 py-1.5 rounded-full font-bold tracking-widest"
          style={{ background: `${c}18`, color: c, border: `1px solid ${c}30`, fontSize: 12 }}
        >
          {CLASSIFICATION_LABEL[score.classification]}
        </span>
        <div className="text-right">
          <div style={{ color: wl.text, fontWeight: 800, fontSize: 22 }}>
            {(score.risk_score * 100).toFixed(0)}%
          </div>
          <div style={{ color: wl.textMuted, fontSize: 10.5, letterSpacing: '0.04em' }}>RISK SCORE</div>
        </div>
      </div>

      <SignalRow label="Exact handle match" value={score.exact_handle_match} />
      <ThresholdBar
        label="Near handle match (Jaro-Winkler)"
        value={score.handle_similarity}
        threshold={T.nearHandle}
        detail={score.near_handle_match ? `${(score.handle_similarity * 100).toFixed(0)}% — near-match` : undefined}
      />
      <ThresholdBar
        label="Display name similarity"
        value={score.display_name_similarity}
        threshold={T.highNameSimilarity}
      />
      <SignalRow
        label="Face match"
        value={score.face_match}
        detail={score.face_match_score != null ? `${(score.face_match_score * 100).toFixed(0)}% similar` : undefined}
      />
      <SignalRow
        label="Logo match"
        value={score.logo_match}
        detail={score.logo_match_score != null ? `${(score.logo_match_score * 100).toFixed(0)}% similar` : undefined}
      />
      <SignalRow label="Payment identifier in bio" value={score.payment_id_in_bio} />

      {override && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${wl.borderSoft}` }}>
          {!overriding ? (
            <button onClick={() => setOverriding(true)} style={{ color: wl.green, fontSize: 11.5, fontFamily: wl.font }}>
              Override classification →
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              {OVERRIDE_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  disabled={saving}
                  onClick={() => applyOverride(opt)}
                  className="px-2.5 py-1 rounded font-semibold"
                  style={{ background: `${classificationColor(opt)}18`, color: classificationColor(opt), border: `1px solid ${classificationColor(opt)}40`, fontSize: 11 }}
                >
                  {CLASSIFICATION_LABEL[opt]}
                </button>
              ))}
              <button onClick={() => setOverriding(false)} style={{ color: wl.textFaint, fontSize: 11 }}>Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
